import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Button,
  Skeleton,
  Stack,
} from '@mui/material';
import { CloseIcon, NotificationsIcon, BusinessIcon, ArrowForwardIcon } from '@/components/common/icons';
import { formatTimeAgo } from '@/utils';
import type { Notification } from '@/features/notifications/types';
import { useNotificationDialog } from '../hooks';
import { notificationDialogStyles as styles } from '../styles';

interface NotificationDialogProps {
  open: boolean;
  onClose: () => void;
  onCountUpdate?: (count: number) => void;
  onMarkAsRead?: () => void;
}

const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
  return (
    <ListItem sx={styles.dialogListItem}>
      <ListItemAvatar sx={{ minWidth: 'auto' }}>
        <Avatar
          src={notification.data?.companyLogoUrl}
          sx={styles.dialogAvatar}
        >
          {notification.data?.companyLogoUrl ? null : <BusinessIcon sx={{ fontSize: 16 }} />}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={styles.dialogTitleText}
            >
              {notification.title}
            </Typography>
            <Typography variant="caption" sx={styles.dialogTimeText}>
              {formatTimeAgo(notification.createdOn)}
            </Typography>
          </Stack>
        }
        secondary={
          <Typography variant="caption" sx={styles.dialogBodyText}>
            {notification.body}
          </Typography>
        }
      />
    </ListItem>
  );
};

const NotificationSkeleton: React.FC = () => (
  <ListItem sx={styles.dialogSkeletonListItem}>
    <Skeleton
      variant="circular"
      width={36}
      height={36}
      sx={styles.dialogSkeletonCircle}
    />
    <Box sx={{ flex: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width="60%" height={16} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" width={40} height={12} sx={{ borderRadius: 1 }} />
      </Stack>
      <Skeleton variant="text" width="80%" height={12} sx={{ mt: 0.5, borderRadius: 1 }} />
    </Box>
  </ListItem>
);

export const NotificationDialog: React.FC<NotificationDialogProps> = ({
  open,
  onClose,
  onCountUpdate,
  onMarkAsRead,
}) => {
  const hook = useNotificationDialog(onClose, onCountUpdate, onMarkAsRead);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      PaperProps={{ sx: styles.paper }}
      slotProps={{ backdrop: { sx: styles.backdrop } }}
    >
      {/* Header */}
      <DialogTitle sx={styles.dialogTitle}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={styles.headerAvatar}>
            <NotificationsIcon sx={{ color: 'white', fontSize: 16 }} />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={styles.headerTitle}>
              Notifications
            </Typography>
            {hook.totalCount > 0 && (
              <Typography variant="caption" sx={styles.headerCount}>
                {hook.totalCount} notification{hook.totalCount !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={styles.content}>
        {hook.isLoading ? (
          <List disablePadding>
            {[1, 2, 3, 4, 5].map((i) => (
              <React.Fragment key={i}>
                <NotificationSkeleton />
                {i < 5 && <Divider sx={styles.divider} />}
              </React.Fragment>
            ))}
          </List>
        ) : hook.isError ? (
          <Box sx={styles.errorContainer}>
            <Avatar sx={styles.errorAvatar}>
              <NotificationsIcon sx={{ fontSize: 20, color: 'inherit' }} />
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={styles.errorTitle}>
              Failed to load
            </Typography>
            <Typography variant="caption" sx={styles.errorSubtitle}>
              Please try again later
            </Typography>
          </Box>
        ) : hook.notifications.length === 0 ? (
          <Box sx={styles.emptyContainer}>
            <Avatar sx={styles.emptyAvatar}>
              <NotificationsIcon sx={{ fontSize: 24, color: 'inherit' }} />
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={styles.emptyTitle}>
              No notifications yet
            </Typography>
            <Typography variant="caption" sx={styles.emptySubtitle}>
              You'll see new notifications here
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {hook.notifications.map((notification, index) => (
              <React.Fragment key={notification.uId}>
                <NotificationItem notification={notification} />
                {index < hook.notifications.length - 1 && <Divider sx={styles.divider} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      {/* Footer - View All Button */}
      {hook.notifications.length > 0 && (
        <Box sx={styles.footer}>
          <Button
            fullWidth
            variant="text"
            onClick={hook.handleViewAll}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={styles.viewAllButton}
          >
            View All Notifications
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default NotificationDialog;
